/**
 * EL SHELL: BARRA LATERAL, CABECERA Y MIGA
 *
 * La navegación es la de un producto de gestión, no la de un panel de
 * demostración: lateral fija agrupada por dominio, y en la cabecera la
 * miga que dice dónde estás.
 *
 * DRAWER MÓVIL, MISMO DOM (FASE 3.3-D1)
 *
 * En pantalla estrecha (≤900px) el `<aside className="lateral">` no
 * cambia de contenido: sigue siendo el mismo `<nav>` con los mismos
 * grupos y el mismo pie de identidad. Sólo cambia, por CSS, de columna
 * fija a panel fuera de pantalla que se desliza como drawer sobre un
 * backdrop. No existe un segundo árbol de enlaces «para móvil»: sería
 * exactamente el error que este fichero evita en la sección de abajo,
 * aplicado a la forma en vez de al contenido.
 *
 * JERARQUÍA ESCALABLE, NO HARDCODEADA POR ROL
 *
 * `NAV` es la única fuente de la estructura del menú: grupo + entradas,
 * cada entrada con el permiso que la habilita. Ni aquí ni en ningún sitio
 * se pregunta «¿es ADMIN?» — se pregunta `puede(permiso)`, que es lo mismo
 * que decide la API. Un grupo entero desaparece si ninguna de sus entradas
 * es visible: así no queda un título de sección sin nada debajo.
 *
 * Faltan a propósito entradas para Tipos de limpieza, Ubicaciones y
 * Planes: hoy esas pantallas solo existen anidadas bajo un cliente o
 * centro concreto (no hay un listado global al que enlazar). Polivalencias
 * sí tiene, desde 3.2B-iv-b, una pantalla global (`/polivalencias`) y por
 * eso es la primera entrada de MAESTROS. Órdenes de trabajo se suma en
 * OPERACIÓN desde 3.3-A (`/ots`, listado global con el mismo `Recorte` de
 * siempre). Añadir el resto en cuanto exista su vista global es sólo
 * tocar `NAV`.
 *
 * INICIO NO ES UN GRUPO
 *
 * Es la única entrada que no se gatea por permiso: con sesión abierta
 * siempre hay algo que ver en `/` (ver `paginas/inicio.tsx`), aunque sea
 * sólo una bienvenida — OPERARIO y CLIENTE, con `PERMISOS_POR_ROL: []`,
 * se quedarían sin una sola entrada de menú si Inicio dependiera de un
 * permiso como el resto.
 */
import { Link, NavLink, useLocation, useNavigate, type To } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useBranding, useSesion } from '../contexto.tsx';
import { useEsMovil } from './base.tsx';
import { IndicadorSync, useSalidaConPendientes } from './indicador-sync.tsx';
import { etiquetaRol } from '../roles.ts';
import { DOMINIOS, pestanaActiva, PestanasDominio, pestanasVisibles, type Dominio } from './dominios.tsx';

export interface EntradaNav {
  readonly texto: string;
  readonly a: string;
  /** Permiso que habilita la entrada. La API decide lo mismo con el mismo nombre. */
  readonly permiso: string;
  /**
   * F13.7: la entrada es un DOMINIO con pestañas (componentes/dominios.tsx): se ve si se puede abrir alguna de
   * ellas, lleva a la primera que se puede y está activa en cualquiera de sus rutas. `a` y `permiso` no se usan.
   */
  readonly dominio?: Dominio;
}

/** ¿Se ve la entrada? (un dominio: si alguna de sus pestañas se puede abrir). */
export function entradaVisible(e: EntradaNav, puede: (p: string) => boolean): boolean {
  return e.dominio === undefined ? puede(e.permiso) : pestanasVisibles(e.dominio, puede).length > 0;
}
/** Adónde lleva la entrada (un dominio: a su primera pestaña permitida). */
export function destinoEntrada(e: EntradaNav, puede: (p: string) => boolean): string {
  return e.dominio === undefined ? e.a : (pestanasVisibles(e.dominio, puede)[0]?.a ?? e.a);
}

export interface GrupoNav {
  readonly titulo: string;
  readonly entradas: readonly EntradaNav[];
}

export const NAV: readonly GrupoNav[] = [
  {
    titulo: 'Operación',
    entradas: [
      //  F10.4: «Planes» (qué se limpia, versiones) y «Planner» (cuándo, el calendario) a la vista;
      //  el Planner lleva apellido porque Mantenimiento tiene el suyo y dos «Planner» confundían.
      { texto: 'Planes', a: '/planes', permiso: 'planes:leer' },
      { texto: 'Planner de limpieza', a: '/planner', permiso: 'ots:leer' },
      { texto: 'Órdenes de trabajo', a: '/ots', permiso: 'ots:leer' },
      //  PORTAL-DEBT-01: pantalla DISTINTA para CLIENTE — nunca aparecen
      //  las dos entradas juntas, porque ningún rol tiene los dos
      //  permisos a la vez (uno es gestión interna, el otro es portal).
      { texto: 'Mis órdenes de trabajo', a: '/mis-ots', permiso: 'ots:leer_propias' },
      //  Portal cliente v2: su calendario de limpieza, sólo consulta (el Planner interno exige `ots:leer`).
      { texto: 'Calendario de limpieza', a: '/portal/planner', permiso: 'ots:leer_propias' },
      //  FASE 7.3: Calidad del PORTAL (sólo CLIENTE) — pantalla propia, nunca la de gestión.
      { texto: 'Calidad', a: '/portal/calidad', permiso: 'calidad:leer_propia' },
      //  FASE 8.4: Mantenimiento del área cliente — módulo aparte de Calidad, nunca la gestión interna.
      { texto: 'Mantenimiento', a: '/portal/mantenimiento', permiso: 'mantenimiento:leer_propio' },
      //  FASE 9.1-C: acceso directo a SUS activos (ficha pública + etiqueta QR). Sólo CLIENTE:
      //  el personal interno tiene «Activos» (gestión) en Mantenimiento y nunca este permiso.
      { texto: 'Mis activos', a: '/portal/mantenimiento/activos', permiso: 'mantenimiento:leer_propio' },
      //  F10.3: su jornada, en línea. `incidencias:reportar` sólo lo tiene OPERARIO: es su marca de rol en
      //  el menú (el dato de /api/mis-ots se autolimita por identidad, no por este permiso).
      { texto: 'Mis trabajos', a: '/mis-ots', permiso: 'incidencias:reportar' },
      //  FASE 7.1: dar aviso, nunca gestionar.
      { texto: 'Reportar incidencia', a: '/reportar', permiso: 'incidencias:reportar' },
      //  FASE 7.2: acciones correctivas de las que es responsable (sin gestionar la NC).
      //  F10.4: «correctivas» — son acciones de Calidad, no su trabajo diario («Mis trabajos»).
      { texto: 'Mis acciones correctivas', a: '/mis-acciones', permiso: 'acciones_correctivas:completar_propias' },
    ],
  },
  {
    //  FASE 7.1: `incidencias:gestionar`, no `:leer` — CLIENTE tiene `:leer`
    //  (sólo lo visible, por RLS) pero ésta es la pantalla de gestión interna.
    titulo: 'Calidad',
    entradas: [{ texto: 'Incidencias y NC', a: '/calidad', permiso: 'incidencias:gestionar' }],
  },
  {
    //  FASE 8.1–8.3: dominio propio, separado de Limpieza. «Incidencias» aquí
    //  son las de MANTENIMIENTO, nunca las de Calidad; «Planner» aquí es el
    //  de Mantenimiento (PT + proyecciones), no el de OT.
    titulo: 'Mantenimiento',
    entradas: [
      { texto: 'Mis partes', a: '/mantenimiento/mis-partes', permiso: 'partes:ejecutar' },
      //  F13.4: el stock de su almacén móvil (sólo si es responsable de uno; si no, la página lo dice).
      { texto: 'Mi furgoneta', a: '/inventario/mi-furgoneta', permiso: 'almacen_movil:propio' },
      { texto: 'Partes de trabajo', a: '/mantenimiento/partes', permiso: 'partes:leer' },
      { texto: 'Preventivos', a: '/mantenimiento/preventivos', permiso: 'preventivos:leer' },
      { texto: 'Planner de mantenimiento', a: '/mantenimiento/planner', permiso: 'partes:leer' },
      { texto: 'Incidencias', a: '/mantenimiento/incidencias', permiso: 'incidencias_mnt:leer' },
      //  F12.4: seguimiento operativo (gestión). El técnico no lo tiene.
      { texto: 'Indicadores', a: '/mantenimiento/indicadores', permiso: 'mantenimiento:indicadores' },
      { texto: 'Activos', a: '/activos', permiso: 'activos:leer' },
      { texto: 'Tipos de activo', a: '/tipos-activo', permiso: 'tipos_activo:gestionar' },
    ],
  },
  {
    //  FASE 9.1: dominio TRANSVERSAL (Limpieza y Mantenimiento). FASE 9.2:
    //  Stock (saldos) y Movimientos (operaciones + ledger). FASE 9.3:
    //  Inventarios físicos. FASE 9.4-B: lotes y series NO son módulos de
    //  primer nivel: se consultan dentro de Stock («Por lote» / «Por serie»),
    //  se usan en Movimientos y aparecen en la ficha del artículo.
    //  F13.7: el menú representa DOMINIOS de trabajo, no funcionalidades. Stock, kárdex y cierres son pestañas de
    //  Movimientos; exportar es una acción contextual (Movimientos, Stock, Cierre); importar, una acción de
    //  Artículos; las furgonetas, una pestaña de Almacenes. «Mi furgoneta» sigue en Mantenimiento (acceso del técnico).
    titulo: 'Inventario',
    entradas: [
      { texto: 'Artículos', a: '/inventario/articulos', permiso: 'articulos:leer', dominio: 'articulos' },
      { texto: 'Almacenes', a: '/inventario/almacenes', permiso: 'almacenes:leer', dominio: 'almacenes' },
      { texto: 'Movimientos', a: '/inventario/movimientos', permiso: 'movimientos_stock:leer', dominio: 'movimientos' },
      { texto: 'Inventarios', a: '/inventario/inventarios', permiso: 'inventarios:leer' },
    ],
  },
  {
    //  FASE 9.1: Clientes, Personal, Maestros y Usuarios se agrupan aquí
    //  para no saturar el menú (antes eran cuatro grupos de 1–3 entradas).
    titulo: 'Administración',
    entradas: [
      { texto: 'Clientes y centros', a: '/clientes', permiso: 'clientes:leer' },
      { texto: 'Operarios', a: '/operarios', permiso: 'operarios:leer' },
      { texto: 'Polivalencias', a: '/polivalencias', permiso: 'polivalencias:leer' },
      { texto: 'Documentos', a: '/documentos', permiso: 'documentos:leer' },
      //  FASE 6.3 §E: mismo permiso que ya gobierna el calendario de un
      //  centro (`calendario:editar`) — ADMIN/CONSULTOR, nunca ENCARGADO.
      { texto: 'Plantillas de festivos', a: '/plantillas-calendario', permiso: 'calendario:editar' },
      { texto: 'Usuarios', a: '/usuarios', permiso: 'usuarios:leer' },
    ],
  },
];

/**
 * FASE 9.1 — GRUPOS PLEGABLES
 *
 * Cada grupo es un botón que pliega/despliega sus entradas. Por defecto
 * todos desplegados; lo que el usuario pliega se recuerda en este
 * navegador (sólo títulos de grupo: nada sensible, y si el almacenamiento
 * falla simplemente no se recuerda). El grupo de la pantalla activa
 * siempre se muestra desplegado. En móvil (drawer) funciona como
 * acordeón: abrir un grupo pliega los demás, para no tener que desplazar
 * una lista larga con una mano. El menú NUNCA es la seguridad: cada
 * entrada sigue gateada por `puede()` y la API decide lo mismo.
 */
const CLAVE_PLEGADOS = 'neatos.nav.plegados';

function leerPlegados(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_PLEGADOS) ?? '[]') as unknown;
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function guardarPlegados(s: ReadonlySet<string>): void {
  try {
    localStorage.setItem(CLAVE_PLEGADOS, JSON.stringify([...s]));
  } catch {
    //  Sin almacenamiento (modo privado, cuota): no se recuerda, sin más.
  }
}

function entradaActiva(e: EntradaNav, ruta: string): boolean {
  if (e.dominio !== undefined) return DOMINIOS[e.dominio].pestanas.some((p) => pestanaActiva(p, ruta));
  return ruta === e.a || ruta.startsWith(`${e.a}/`);
}

export function Marca({ pequeno = false, variante = 'normal' }: { pequeno?: boolean; variante?: 'normal' | 'inverso' }): ReactNode {
  const b = useBranding();
  //  Identidad del producto, no del cliente. Los logotipos de cliente se
  //  resuelven exclusivamente en Avatar/GestorLogoCliente.
  //
  //  Ficheros recortados, nunca uno reescalado ni filtrado por CSS (branding
  //  definitivo, FASE 5.7-C): `neatos-logo-compacto.png` para la franja
  //  estrecha sobre fondo CLARO (cabecera móvil); `neatos-logo.png` para el
  //  resto (login, PIN). UX-1A: `neatos-logo-inverso.png` (letras blancas,
  //  recortado de docs/NeatOS_Blanco.png) para la navegación OSCURA: el
  //  compacto tiene las letras negras y sobre el lateral no se leía (1,3:1).
  const src = variante === 'inverso' ? '/neatos-logo-inverso.png' : pequeno ? '/neatos-logo-compacto.png' : '/neatos-logo.png';
  return (
    <img
      className={`marca-neatos${pequeno ? ' pequeno' : ''}${variante === 'inverso' ? ' inverso' : ''}`}
      src={src}
      alt={b.nombre}
      data-variante={variante}
    />
  );
}

export interface Miga {
  readonly texto: string;
  readonly a?: To;
  /** F10.2: estado de navegación a llevar al volver (p. ej. el «volver» de la OT a «Hoy»). */
  readonly state?: unknown;
}

/**
 * UX-1B — CABECERA DE PÁGINA
 *
 * Responde en un vistazo: dónde estoy (migas), qué veo (título y código), en
 * qué estado (estado), datos clave en una línea (meta) y qué puedo hacer
 * (acciones: una principal, como mucho una secundaria y «Más acciones»). Las
 * pestañas, debajo y separadas de las acciones. NO es fija: en móvil sólo la
 * barra superior lo es. Puramente presentacional: cada página decide qué
 * pasa en cada hueco (nunca reglas de negocio aquí).
 */
export function PageHeader({ migas, titulo, estado, meta, subtitulo, acciones, tabs, dominio }: {
  migas?: readonly Miga[];
  /** F13.7: pestañas del dominio (Movimientos · Stock · Kárdex · Cierres…), sobre el título. */
  dominio?: Dominio;
  titulo: ReactNode;
  /** Etiqueta de estado junto al título. */
  estado?: ReactNode;
  /** Línea compacta de metadatos (código · centro · fecha…); envuelve en móvil. */
  meta?: ReactNode;
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  tabs?: ReactNode;
}): ReactNode {
  return (
    <header className="cabecera">
      {migas !== undefined && migas.length > 0 && (
        <nav className="miga" aria-label="Ruta">
          {migas.map((m, i) => (
            <span key={i}>{m.a === undefined ? m.texto : <NavLink to={m.a} state={m.state}>{m.texto}</NavLink>}</span>
          ))}
        </nav>
      )}
      {dominio !== undefined && <PestanasDominio dominio={dominio} />}
      <div className="cabecera-fila">
        <div className="cabecera-titulo">
          <h1>{titulo}</h1>
          {estado !== undefined && <span className="cabecera-estado">{estado}</span>}
        </div>
        {acciones !== undefined && <div className="cabecera-acciones">{acciones}</div>}
      </div>
      {meta !== undefined && <div className="cabecera-meta suave">{meta}</div>}
      {subtitulo !== undefined && <div className="cabecera-subtitulo suave">{subtitulo}</div>}
      {tabs}
    </header>
  );
}

export function Shell({
  migas,
  titulo,
  estado,
  meta,
  subtitulo,
  acciones,
  tabs,
  dominio,
  children,
}: {
  migas?: readonly Miga[];
  /** F13.7: dominio de la pantalla (pinta sus pestañas). */
  dominio?: Dominio;
  titulo: ReactNode;
  /** UX-1B: estado junto al título (ver PageHeader). */
  estado?: ReactNode;
  /** UX-1B: línea compacta de metadatos (ver PageHeader). */
  meta?: ReactNode;
  /**
   * Línea de contexto bajo el título (código, cliente enlazado, estado…).
   * `Shell` no sabe qué es una entidad ni un rol: cada página compone su
   * propio `subtitulo` — esto es puramente presentacional, sólo decide
   * DÓNDE se pinta, nunca QUÉ.
   */
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  tabs?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const b = useBranding();
  const { usuario, puede, salir } = useSesion();
  //  F14.1 — salir con cambios offline sin enviar nunca es silencioso.
  const { pedirSalida, dialogo: dialogoSalida } = useSalidaConPendientes(usuario, async () => {
    await salir();
    navegar('/entrar');
  });
  const navegar = useNavigate();
  const location = useLocation();

  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [plegados, setPlegados] = useState<Set<string>>(leerPlegados);
  const esMovil = useEsMovil(900);
  const botonMenuRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const abiertoAntes = useRef(false);

  function cerrarDrawer(): void {
    setDrawerAbierto(false);
  }

  //  Cambiar de pantalla mientras el drawer está abierto —una pestaña, un
  //  breadcrumb, un botón, un `navigate()` programático— no debe dejarlo
  //  abierto sobre la vista nueva. Estado local a `Shell`, no global: en
  //  cuanto cambia la ruta, se cierra. Cubre también el caso en que la
  //  propia `Shell` no se desmonta (por ejemplo, cambiar de pestaña bajo
  //  `DisposicionOperario`/`DisposicionCentro`), que es donde este efecto
  //  aporta algo que un simple `key` de remontaje no daría.
  useEffect(() => {
    setDrawerAbierto(false);
  }, [location.pathname]);

  //  UX-1A: la entrada activa no puede quedar escondida bajo el pie del lateral
  //  (a 768 px de alto, «Stock» quedaba fuera). Sólo se desplaza si de verdad
  //  está fuera del área visible del menú: sin saltos innecesarios.
  useEffect(() => {
    const nav = navRef.current;
    const activa = nav?.querySelector<HTMLElement>('a.activo');
    if (nav == null || activa == null) return;
    const n = nav.getBoundingClientRect();
    const a = activa.getBoundingClientRect();
    if (a.top < n.top || a.bottom > n.bottom) activa.scrollIntoView?.({ block: 'nearest' });
  }, [location.pathname]);

  //  Bloqueo de scroll del fondo mientras el drawer está abierto: un único
  //  estilo, y se restaura al valor que tuviera antes (no a `''` a ciegas)
  //  al cerrar o al desmontar.
  useEffect(() => {
    if (!drawerAbierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [drawerAbierto]);

  //  Escape cierra el drawer.
  useEffect(() => {
    if (!drawerAbierto) return;
    function alTeclado(e: KeyboardEvent): void {
      if (e.key === 'Escape') setDrawerAbierto(false);
    }
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [drawerAbierto]);

  //  Foco mínimo accesible, sin librería de focus-trap: al abrir, al
  //  primer enlace de navegación; al cerrar, de vuelta al botón que lo
  //  abrió. No es una trampa de foco completa —un Tab insistente puede
  //  salir del drawer hacia el resto del documento—, pero con Escape +
  //  backdrop + este movimiento de foco un usuario de teclado nunca queda
  //  perdido sin saber dónde está.
  useEffect(() => {
    if (drawerAbierto && !abiertoAntes.current) {
      navRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    } else if (!drawerAbierto && abiertoAntes.current) {
      botonMenuRef.current?.focus();
    }
    abiertoAntes.current = drawerAbierto;
  }, [drawerAbierto]);

  //  `?? []` sobre `roles` y no sólo sobre `usuario`: si la API devolviera
  //  alguna vez un usuario sin roles, el shell entero dejaría de pintarse y
  //  la pantalla se quedaría en blanco. Una barra lateral sin la lista de
  //  roles es un defecto menor; una página en blanco, no.
  const rolesVisibles = [...new Set((usuario?.roles ?? []).map((r) => etiquetaRol(r.rol)))].join(
    ' · ',
  );

  //  Cada grupo se filtra por permiso real; el grupo entero se omite si se
  //  queda sin entradas visibles, para no dejar un título de sección vacío.
  const gruposVisibles = NAV.map((g) => ({
    ...g,
    entradas: g.entradas.filter((e) => entradaVisible(e, puede)),
  })).filter((g) => g.entradas.length > 0);
  const grupoActivo = gruposVisibles.find((g) => g.entradas.some((e) => entradaActiva(e, location.pathname)))?.titulo ?? null;
  const desplegado = (titulo: string): boolean => titulo === grupoActivo || !plegados.has(titulo);

  function alternarGrupo(titulo: string): void {
    setPlegados((prev) => {
      const abrir = prev.has(titulo);
      let sig: Set<string>;
      if (abrir) {
        sig = new Set(prev);
        sig.delete(titulo);
        //  Acordeón en móvil: abrir uno pliega los demás (salvo el de la pantalla activa).
        if (esMovil) for (const g of gruposVisibles) if (g.titulo !== titulo && g.titulo !== grupoActivo) sig.add(g.titulo);
      } else {
        sig = new Set(prev);
        sig.add(titulo);
      }
      guardarPlegados(sig);
      return sig;
    });
  }

  return (
    <div className="shell">
      {/* UX-1A: en móvil, cerrado = fuera del árbol de foco y de accesibilidad (inert); en escritorio siempre activo. */}
      <aside id="lateral-navegacion" className={`lateral${drawerAbierto ? ' abierto' : ''}`} inert={esMovil && !drawerAbierto}>
        <Link to="/" className="lateral-marca" aria-label={`Ir al inicio de ${b.nombre}`}>
          <Marca pequeno variante="inverso" />
          <span className="sr-solo">{b.nombreCorto}</span>
        </Link>

        <nav aria-label="Principal" ref={navRef}>
          {usuario !== null && (
            <NavLink
              to="/"
              end
              className={({ isActive }) => (isActive ? 'activo' : '')}
              onClick={cerrarDrawer}
            >
              Inicio
            </NavLink>
          )}
          {gruposVisibles.map((g) => {
            const abierto = desplegado(g.titulo);
            const idLista = `nav-grupo-${g.titulo.normalize('NFD').replace(/[^\w]/g, '').toLowerCase()}`;
            return (
              <div key={g.titulo} className="nav-grupo">
                <button
                  type="button"
                  className="grupo"
                  aria-expanded={abierto}
                  aria-controls={idLista}
                  onClick={() => alternarGrupo(g.titulo)}
                >
                  <span className="grupo-flecha" aria-hidden="true">{abierto ? '▾' : '▸'}</span>
                  {g.titulo}
                </button>
                {abierto && (
                  <div id={idLista}>
                    {g.entradas.map((e) => (
                      <NavLink
                        key={e.a}
                        to={destinoEntrada(e, puede)}
                        className={({ isActive }) => (isActive || entradaActiva(e, location.pathname) ? 'activo' : '')}
                        aria-current={entradaActiva(e, location.pathname) ? 'page' : undefined}
                        onClick={cerrarDrawer}
                      >
                        {e.texto}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="lateral-pie">
          {/* F14.1 — también sin usuario (trabajo offline con el perfil desbloqueado tras recargar sin red). */}
          <IndicadorSync usuario={usuario} />
          {usuario !== null && (
            <>
              <div className="quien">
                {usuario.nombre} {usuario.apellidos}
              </div>
              <div className="rol">{rolesVisibles}</div>
              <Link to="/cambiar-password" className="enlace">
                Cambiar contraseña
              </Link>
              <button onClick={pedirSalida}>
                Salir
              </button>
            </>
          )}
        </div>
      </aside>

      {drawerAbierto && <div className="drawer-backdrop" aria-hidden="true" onClick={cerrarDrawer} />}

      {/*
        Cabecera compacta de móvil: mismo `NAV`/`puede()` de siempre, sólo
        un botón que abre el `<aside>` de arriba como drawer. En escritorio
        queda oculta por CSS y no reserva espacio.
      */}
      <header className="cabecera-movil">
        <button
          type="button"
          ref={botonMenuRef}
          className="boton-menu"
          aria-label={drawerAbierto ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={drawerAbierto}
          aria-controls="lateral-navegacion"
          onClick={() => setDrawerAbierto((v) => !v)}
        >
          ☰
        </button>
        <Link to="/" className="marca-movil">
          <Marca pequeno />
          <span className="sr-solo">{b.nombreCorto}</span>
        </Link>
        <span className="cabecera-movil-sync"><IndicadorSync usuario={usuario} compacto /></span>
      </header>
      {dialogoSalida}

      <main className="principal">
        <PageHeader migas={migas} titulo={titulo} estado={estado} meta={meta} subtitulo={subtitulo} acciones={acciones} tabs={tabs} dominio={dominio} />
        <div className="contenido">{children}</div>
      </main>
    </div>
  );
}
